import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { AlertTriangle, Info, Lightbulb } from 'lucide-react';

/**
 * Komponentová knihovna design systému (Fluent 2, závazná paleta zadavatele —
 * viz index.css). Velké ovládací prvky (checklist H1: dotykový cíl min. 48 px
 * pro terén — palec v rukavici), kontrast ≥ 4,5:1 (ux-omezeni §1: ~2/3
 * uživatelů 50+). Kancelářský desktop smí být kompaktnější (Btn
 * size="desktop"), terén VŽDY 48+.
 *
 * Pravidla palety: na akcentní oranžové VŽDY tmavý text (text-text-heading),
 * plné stavové barvy (bg-error, bg-info…) nikdy nenesou text — bannery
 * a badge jsou vždy tint pozadí + textový odstín (+ border u bannerů).
 */

type BtnVarianta = 'primary' | 'accent' | 'outline' | 'subtle' | 'danger' | 'secondary';
type BtnVelikost = 'teren' | 'desktop';

const BTN_VARIANTY: Record<BtnVarianta, string> = {
  primary:
    'bg-primary text-white shadow-sm hover:bg-primary-hover active:bg-primary-pressed disabled:bg-fill-disabled disabled:text-text-disabled disabled:shadow-none',
  /** Akcent — VŽDY tmavý text (#1A1A1A), i v hover/pressed stavu. */
  accent:
    'bg-accent text-text-heading shadow-sm hover:bg-accent-hover active:bg-accent-pressed disabled:bg-fill-disabled disabled:text-text-disabled disabled:shadow-none',
  outline:
    'border border-border bg-surface text-text-primary shadow-sm hover:bg-bg active:bg-primary-tint disabled:border-border disabled:bg-fill-disabled disabled:text-text-disabled disabled:shadow-none',
  subtle:
    'bg-transparent text-primary hover:bg-primary-tint active:bg-primary-tint disabled:text-text-disabled',
  /** Destruktivní akce — tint styl (bílý text na plné error nemá kontrast). */
  danger:
    'border border-error-border bg-surface text-error-text hover:bg-error-bg active:bg-error-bg disabled:border-border disabled:bg-fill-disabled disabled:text-text-disabled',
  /** @deprecated alias pro `outline` (starší obrazovky). */
  secondary:
    'border border-border bg-surface text-text-primary shadow-sm hover:bg-bg active:bg-primary-tint disabled:border-border disabled:bg-fill-disabled disabled:text-text-disabled disabled:shadow-none',
};

/** Výchozí = terén (H1: ≥48 px); desktop (myš v kanceláři) je kompaktnější. */
const BTN_VELIKOSTI: Record<BtnVelikost, string> = {
  teren: 'min-h-12 min-w-12 px-4 text-base',
  desktop: 'min-h-10 px-3.5 text-sm',
};

export function Btn({
  varianta = 'primary',
  size = 'teren',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { varianta?: BtnVarianta; size?: BtnVelikost }) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors duration-100 disabled:cursor-not-allowed ${BTN_VELIKOSTI[size]} ${BTN_VARIANTY[varianta]} ${className}`}
    />
  );
}

/**
 * Rozbalovací menu (dropdown) — jeden sdílený vzor pro „⋯ Další akce", „Export"
 * apod. Nahrazuje ruční `<details>`: panel je VŽDY `absolute` (nikdy netlačí
 * obsah = žádné poskočení layoutu), zavírá se kliknutím mimo i Escapem, má
 * minimální šířku a defaultně se zarovnává doprava (nepřeteče pravý okraj karty).
 * `spoust` = vlastní styl tlačítka (výchozí = decentní „⋯"); `align` = strana.
 */
export function Menu({
  stitek,
  align = 'right',
  spoust,
  zavriPoKliku = true,
  children,
}: {
  stitek: ReactNode;
  align?: 'left' | 'right';
  spoust?: string;
  /** Zavřít po kliknutí uvnitř? Vypni u menu s formulářovými poli (filtr, select). */
  zavriPoKliku?: boolean;
  children: ReactNode;
}) {
  const [otevreno, setOtevreno] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!otevreno) return;
    const naDokument = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOtevreno(false);
    };
    const naKlavesu = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOtevreno(false);
    };
    document.addEventListener('mousedown', naDokument);
    document.addEventListener('keydown', naKlavesu);
    return () => {
      document.removeEventListener('mousedown', naDokument);
      document.removeEventListener('keydown', naKlavesu);
    };
  }, [otevreno]);

  const spoustVychozi =
    'inline-flex min-h-11 cursor-pointer items-center gap-1 rounded-md px-3 text-base font-medium text-primary transition-colors duration-100 hover:bg-primary-tint';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={otevreno}
        className={spoust ?? spoustVychozi}
        onClick={() => setOtevreno((o) => !o)}
      >
        {stitek}
      </button>
      {otevreno && (
        <div
          role="menu"
          className={`absolute z-30 mt-1 flex min-w-56 max-w-[calc(100vw-2rem)] flex-col gap-1 rounded-lg border border-border bg-surface p-2 shadow-lg ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
          onClick={zavriPoKliku ? () => setOtevreno(false) : undefined}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/** Sdílený vzhled formulářových polí: border → border-strong (hover) → primary (focus). */
const POLE_VZHLED =
  'w-full rounded-lg border border-border bg-surface text-base text-text-primary shadow-sm transition-colors duration-100 placeholder:text-text-muted hover:border-border-strong focus:border-primary focus:outline-2 focus:outline-primary disabled:border-border disabled:bg-fill-disabled disabled:text-text-disabled disabled:shadow-none';

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`min-h-12 px-3 ${POLE_VZHLED} ${className}`} />;
}

export function Select({ className = '', ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`min-h-12 px-3 ${POLE_VZHLED} ${className}`} />;
}

/**
 * Textarea: `field-sizing: content` (Baseline 2024) nechá pole růst s obsahem,
 * takže na úzkém telefonu (390 px) nevzniká vnitřní scrollbar ani se neořízne
 * placeholder. `rows` slouží jen jako fallback výchozí výšky pro prohlížeče bez
 * field-sizing; `min-h`/`max-h` drží pole v rozumných mezích.
 */
export function Textarea({
  className = '',
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={`field-sizing-content max-h-72 min-h-24 p-3 ${POLE_VZHLED} ${className}`}
    />
  );
}

export function Pole({
  popisek,
  children,
  povinne = false,
  napoveda,
}: {
  popisek: string;
  children: ReactNode;
  povinne?: boolean;
  /** Krátká věta pod popiskem (formát, odkud se doplní…) — IxDF: pomoc u pole. */
  napoveda?: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-text-secondary">
        {popisek}
        {povinne ? <span className="text-error-text"> *</span> : null}
        {napoveda ? <span className="ml-2 font-normal text-text-muted">{napoveda}</span> : null}
      </span>
      {children}
    </label>
  );
}

// --- Stavové prvky: banner + badge (vždy tint pozadí + textový odstín) ---

type BannerTon = 'info' | 'success' | 'warning' | 'error';

const BANNER_TONY: Record<BannerTon, { box: string; ikona: string; role: 'status' | 'alert' }> = {
  info: { box: 'border-info-border bg-info-bg text-info-text', ikona: 'ⓘ', role: 'status' },
  success: {
    box: 'border-success-border bg-success-bg text-success-text',
    ikona: '✓',
    role: 'status',
  },
  warning: {
    box: 'border-warning-border bg-warning-bg text-warning-text',
    ikona: '⚠',
    role: 'status',
  },
  error: { box: 'border-error-border bg-error-bg text-error-text', ikona: '⚠', role: 'alert' },
};

/**
 * Stavový banner (Fluent MessageBar): tint pozadí + border + textový odstín
 * + ikona. Error má role="alert" (čtečky i testy), ostatní role="status".
 */
export function Banner({
  ton = 'info',
  className = '',
  children,
}: {
  ton?: BannerTon;
  className?: string;
  children: ReactNode;
}) {
  const t = BANNER_TONY[ton];
  return (
    <div
      role={t.role}
      className={`flex items-start gap-2 rounded-md border p-3 text-base ${t.box} ${className}`}
    >
      <span aria-hidden="true" className="shrink-0 leading-6">
        {t.ikona}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** Chybová hláška (role="alert" kvůli čtečkám i testům). */
export function Chyba({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <Banner ton="error">{children}</Banner>;
}

export function Nacitani({ text = 'Načítám…' }: { text?: string }) {
  return <p className="p-4 text-text-secondary">{text}</p>;
}

type CalloutTon = 'info' | 'warning' | 'error';
const CALLOUT_HRANA: Record<CalloutTon, string> = {
  info: 'border-l-info',
  warning: 'border-l-warning',
  error: 'border-l-error',
};
const CALLOUT_CHIP: Record<CalloutTon, string> = {
  info: 'bg-info-bg text-info-text',
  warning: 'bg-warning-bg text-warning-text',
  error: 'bg-error-bg text-error-text',
};

/**
 * Klidné upozornění (hlídač) — jemná karta s barevnou LEVOU hranou dle
 * závažnosti a SVG ikonou v tint chipu, NE barevná zeď (Banner). Informuje /
 * nabídne akci, aniž láme flow. Jeden vzor pro hlídače napříč aplikací.
 */
export function Callout({
  ton = 'info',
  ikona,
  children,
}: {
  ton?: CalloutTon;
  ikona?: ReactNode;
  children: ReactNode;
}) {
  const vychozi =
    ton === 'error' ? (
      <AlertTriangle size={16} strokeWidth={2} />
    ) : ton === 'warning' ? (
      <Lightbulb size={16} strokeWidth={2} />
    ) : (
      <Info size={16} strokeWidth={2} />
    );
  return (
    <section
      role="status"
      className={`flex items-start gap-3 rounded-lg border border-border border-l-4 bg-surface p-4 shadow-sm ${CALLOUT_HRANA[ton]}`}
    >
      <span
        aria-hidden="true"
        className={`flex size-7 shrink-0 items-center justify-center rounded-full ${CALLOUT_CHIP[ton]}`}
      >
        {ikona ?? vychozi}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </section>
  );
}

/**
 * Kostra načítání (skeleton) — jemný pulzující blok. Místo textového „Načítám…"
 * u seznamů/tabulek: obsah „naskočí na místo" (Doherty — vnímaná rychlost;
 * Aesthetic-Usability). Dekorativní: skryté před čtečkami, stav hlásí obal.
 */
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div aria-hidden="true" className={`animate-pulse rounded-md bg-fill-disabled ${className}`} />
  );
}

/** Kostra seznamu/tabulky — N řádků (výchozí výška odpovídá řádku dotykového cíle). */
export function SkeletonSeznam({ radku = 5, vyska = 'h-14' }: { radku?: number; vyska?: string }) {
  return (
    <div role="status" aria-busy="true" className="flex flex-col gap-3">
      <span className="sr-only">Načítám…</span>
      {Array.from({ length: radku }).map((_, i) => (
        <Skeleton key={i} className={`w-full ${vyska}`} />
      ))}
    </div>
  );
}

/**
 * Sbalovací sekce (progressive disclosure) — jeden sdílený vzor pro „Doplnit",
 * „Další", „Historie" apod. Nahrazuje ručně psané `<details>`/accordiony:
 * konzistentní vzhled i chování napříč appkou (Nielsen #4). Panel je vždy
 * v toku (roste dolů); pro overlay menu slouží `Menu`.
 */
export function Sekce({
  titulek,
  podtitulek,
  vychoziOtevreno = false,
  children,
}: {
  titulek: ReactNode;
  podtitulek?: ReactNode;
  vychoziOtevreno?: boolean;
  children: ReactNode;
}) {
  const [otevreno, setOtevreno] = useState(vychoziOtevreno);
  return (
    <section className="rounded-xl border border-border bg-surface">
      <button
        type="button"
        aria-expanded={otevreno}
        className="flex min-h-14 w-full items-center justify-between gap-2 px-4 text-left text-lg font-bold"
        onClick={() => setOtevreno((o) => !o)}
      >
        <span className="min-w-0">
          {titulek}
          {podtitulek ? (
            <span className="ml-2 text-sm font-normal text-text-secondary">{podtitulek}</span>
          ) : null}
        </span>
        <span aria-hidden="true" className="shrink-0 text-text-secondary">
          {otevreno ? '▲' : '▼'}
        </span>
      </button>
      {otevreno ? <div className="flex flex-col gap-4 p-4 pt-0">{children}</div> : null}
    </section>
  );
}

export type BadgeStav =
  'success' | 'warning' | 'error' | 'info' | 'neutral' | 'primary' | 'accent' | 'sporna';

const BADGE_STAVY: Record<BadgeStav, string> = {
  success: 'bg-success-bg text-success-text',
  warning: 'bg-warning-bg text-warning-text',
  error: 'bg-error-bg text-error-text',
  info: 'bg-info-bg text-info-text',
  neutral: 'bg-fill-disabled text-text-secondary',
  primary: 'bg-primary-tint text-primary',
  /** Akcent — tmavý text, nikdy bílý. */
  accent: 'bg-accent text-text-heading',
  sporna: 'bg-sporna-bg text-sporna-text',
};

/** Stavový badge: tint pozadí + textový odstín (`stav`), příp. tokenové třídy (`barva`). */
export function Badge({
  stav,
  barva,
  children,
}: {
  stav?: BadgeStav;
  barva?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${stav ? BADGE_STAVY[stav] : (barva ?? '')}`}
    >
      {children}
    </span>
  );
}

/** Chip — výběrový prvek (filtr, přepínač): vybraný = primary-tint + navy. */
export function Chip({
  vybrany = false,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { vybrany?: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={vybrany}
      {...props}
      className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-base font-medium transition-colors duration-100 ${
        vybrany
          ? 'border-primary bg-primary-tint text-primary'
          : 'border-border-strong bg-surface text-text-primary hover:bg-bg'
      } ${className}`}
    />
  );
}

export function Karta({
  nadpis,
  akce,
  children,
}: {
  nadpis?: string;
  akce?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-surface p-5 shadow-sm">
      {(nadpis || akce) && (
        <div className="mb-4 flex items-center justify-between gap-4">
          {nadpis ? (
            <h2 className="text-base font-semibold text-text-heading">{nadpis}</h2>
          ) : (
            <span />
          )}
          {akce}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * KPI dlaždice (SaaS dashboard): drobný popisek + velké číslo + detail.
 * Ikona v tint chipu vpravo nahoře. Neklikací obsah — odkaz řeší rodič
 * (obalí ji do <Link className="block">).
 */
export function StatKarta({
  ikona,
  popisek,
  hodnota,
  detail,
  ton = 'neutral',
}: {
  ikona?: ReactNode;
  popisek: string;
  hodnota: ReactNode;
  detail?: ReactNode;
  ton?: 'neutral' | 'warning' | 'error' | 'success';
}) {
  const cislo =
    ton === 'error'
      ? 'text-error-text'
      : ton === 'warning'
        ? 'text-warning-text'
        : ton === 'success'
          ? 'text-success-text'
          : 'text-text-heading';
  return (
    <div className="flex h-full flex-col gap-3 rounded-xl border border-border bg-surface p-5 shadow-sm transition-colors duration-100 hover:border-border-strong">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-text-secondary">{popisek}</p>
        {ikona ? (
          <span
            aria-hidden="true"
            className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-tint text-primary"
          >
            {ikona}
          </span>
        ) : null}
      </div>
      <div>
        <p className={`text-3xl font-bold tracking-tight tabular-nums ${cislo}`}>{hodnota}</p>
        {detail ? <p className="mt-1 text-sm text-text-secondary">{detail}</p> : null}
      </div>
    </div>
  );
}

type PoleKartaTon = 'neutral' | 'hlavni' | 'jemny';

/**
 * Karta jednoho pole/sekce formuláře s HLAVIČKOU (ikona + název + volitelná
 * nápověda a akce). Na rozdíl od uniformních boxů nese VÁHU: `hlavni` = hero
 * pole (silnější rám, laděná hlavička, větší ikona/titulek) pro to nejdůležitější;
 * `jemny` = vedlejší/metadata (tišší). Semantická ikona dává poli význam na
 * první pohled. Tohle je základní stavební blok všech formulářů.
 */
export function PoleKarta({
  ikona,
  nadpis,
  napoveda,
  akce,
  vaha = 'neutral',
  children,
}: {
  /** SVG ikona (lucide) — vykreslí se v tint chipu vlevo od názvu. */
  ikona?: ReactNode;
  nadpis: string;
  napoveda?: ReactNode;
  akce?: ReactNode;
  vaha?: PoleKartaTon;
  children: ReactNode;
}) {
  const hlavni = vaha === 'hlavni';
  const jemny = vaha === 'jemny';
  const ramecek = hlavni
    ? 'rounded-xl border-2 border-primary shadow-md'
    : jemny
      ? 'rounded-xl border border-border'
      : 'rounded-xl border border-border shadow-sm';
  const hlavicka = hlavni ? 'bg-primary-tint py-3' : 'py-3';
  return (
    <section className={`overflow-hidden bg-surface ${ramecek}`}>
      <div className={`flex items-center justify-between gap-3 px-4 ${hlavicka}`}>
        <div className="flex min-w-0 items-center gap-2.5">
          {ikona ? (
            <span
              aria-hidden="true"
              className={`flex shrink-0 items-center justify-center rounded-lg ${
                hlavni ? 'size-9 bg-primary text-white' : 'size-8 bg-primary-tint text-primary'
              }`}
            >
              {ikona}
            </span>
          ) : null}
          <h2
            className={
              hlavni
                ? 'text-lg font-bold text-primary'
                : jemny
                  ? 'text-sm font-semibold text-text-secondary'
                  : 'text-base font-semibold text-text-heading'
            }
          >
            {nadpis}
          </h2>
          {napoveda ? <Napoveda titulek={nadpis}>{napoveda}</Napoveda> : null}
        </div>
        {akce ? <div className="shrink-0">{akce}</div> : null}
      </div>
      <div className="px-4 pb-4">{children}</div>
    </section>
  );
}

/**
 * Fakt (údaj) pro DETAILOVÉ obrazovky — malý VELKÝ popisek + hodnota pod ním.
 * Datový pohled, ne formulář: víc `Fakt` do mřížky `<dl>`. `celaSirka` pro
 * dlouhý text (platební podmínky). Hierarchii dělá hero nad mřížkou, ne pole.
 */
export function Fakt({
  nazev,
  hodnota,
  celaSirka = false,
}: {
  nazev: string;
  hodnota: ReactNode;
  celaSirka?: boolean;
}) {
  return (
    <div className={celaSirka ? 'sm:col-span-2 lg:col-span-3' : ''}>
      <dt className="text-xs font-bold tracking-wide text-text-secondary uppercase">{nazev}</dt>
      <dd className="mt-0.5 text-base text-text-primary">{hodnota}</dd>
    </div>
  );
}

/** Hlavička stránky (SaaS band): breadcrumb/kontext, titulek, akce vpravo. */
export function PageHeader({
  titulek,
  kontext,
  popis,
  akce,
}: {
  titulek: string;
  kontext?: ReactNode;
  popis?: ReactNode;
  akce?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        {kontext ? (
          <div className="mb-0.5 text-xs font-medium text-text-secondary">{kontext}</div>
        ) : null}
        <h1 className="text-xl font-bold tracking-tight text-text-heading">{titulek}</h1>
        {popis ? <p className="mt-0.5 text-sm text-text-secondary">{popis}</p> : null}
      </div>
      {akce ? <div className="flex shrink-0 flex-wrap items-center gap-3">{akce}</div> : null}
    </header>
  );
}

/** Prázdný stav: ikona + titulek + popis + akce (nápověda „co teď udělat"). */
export function EmptyState({
  ikona,
  titulek,
  popis,
  akce,
}: {
  /** SVG ikona (lucide) — vykreslí se v tint kruhu. */
  ikona?: ReactNode;
  titulek: string;
  popis?: string;
  akce?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border-strong bg-surface px-8 py-12 text-center">
      {ikona ? (
        <span
          aria-hidden="true"
          className="flex size-12 items-center justify-center rounded-full bg-primary-tint text-primary"
        >
          {ikona}
        </span>
      ) : null}
      <p className="text-base font-semibold text-text-heading">{titulek}</p>
      {popis ? <p className="max-w-md text-sm text-text-secondary">{popis}</p> : null}
      {akce ? <div className="mt-1">{akce}</div> : null}
    </div>
  );
}

/**
 * Jednoduchý CSS tooltip (hover + focus-within, role="tooltip",
 * aria-describedby na dítěti) — pro krátké nápovědy v kanceláři.
 */
export function Tooltip({ text, children }: { text: string; children: ReactNode }) {
  const id = useId();
  const dite = isValidElement(children)
    ? cloneElement(children as ReactElement<{ 'aria-describedby'?: string }>, {
        'aria-describedby': id,
      })
    : children;
  return (
    <span className="group/tooltip relative inline-flex">
      {dite}
      <span
        role="tooltip"
        id={id}
        className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 rounded-md bg-text-heading px-2.5 py-1.5 text-sm font-medium whitespace-nowrap text-white opacity-0 shadow-md transition-opacity duration-150 group-focus-within/tooltip:opacity-100 group-hover/tooltip:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}

/**
 * Nápověda „?" (radikální simplifikace, blueprint 16): kulaté tlačítko „?"
 * u nadpisu/sekce, kliknutí rozbalí krátký on-demand panel. Vysvětlení není
 * z FUNKCE pryč — jen z OČÍ (default zavřeno, cesta zpět jedním ťuknutím).
 * Bez knihovny, řízené `details`-like stavem pro čitelný test „po kliku na ?".
 */
export function Napoveda({
  titulek,
  className = '',
  children,
}: {
  /** Popisek pro čtečky (aria-label tlačítka), např. „Jak Změna funguje". */
  titulek: string;
  className?: string;
  children: ReactNode;
}) {
  const id = useId();
  const [otevreno, setOtevreno] = useState(false);
  return (
    <span className={`relative inline-flex ${className}`}>
      <button
        type="button"
        aria-label={titulek}
        aria-expanded={otevreno}
        aria-controls={id}
        onClick={() => setOtevreno((o) => !o)}
        className="inline-flex size-6 items-center justify-center rounded-full border border-border-strong bg-surface text-sm font-bold text-text-secondary transition-colors duration-100 hover:bg-primary-tint hover:text-primary"
      >
        ?
      </button>
      {otevreno ? (
        <span
          id={id}
          role="note"
          className="absolute top-8 left-0 z-40 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-info-border bg-info-bg p-3 text-sm text-text-primary shadow-md"
        >
          <span className="mb-1 block font-bold text-info-text">{titulek}</span>
          {children}
        </span>
      ) : null}
    </span>
  );
}

export const th =
  'px-3 py-2.5 text-left text-xs font-semibold tracking-wide text-text-secondary uppercase whitespace-nowrap';
export const td = 'px-3 py-3 align-middle border-t border-border text-sm tabular-nums';

// --- Terén (Blok B): prvky pro rukavice a slunce (checklist H1–H3) ---

type VelkeVarianta = 'primary' | 'secondary' | 'vyrazne' | 'podpis';

const VELKE_VARIANTY: Record<VelkeVarianta, string> = {
  primary:
    'bg-primary text-white hover:bg-primary-hover active:bg-primary-pressed disabled:bg-fill-disabled disabled:text-text-disabled',
  secondary:
    'bg-surface text-text-primary border-2 border-border-strong hover:bg-bg active:bg-primary-tint disabled:border-border disabled:text-text-disabled',
  /** NÁLEZ — výrazné, ale ne „chyba": akcentní oranžová s TMAVÝM textem. */
  vyrazne:
    'bg-accent text-text-heading hover:bg-accent-hover active:bg-accent-pressed disabled:bg-fill-disabled disabled:text-text-disabled',
  /** Podpis dne — vážná, nevratná akce. */
  podpis:
    'bg-text-heading text-white hover:bg-text-primary disabled:bg-fill-disabled disabled:text-text-disabled',
};

/**
 * Velké terénní tlačítko (H1: dotykový cíl min. 48 px — tady 56 px, palec
 * v rukavici): plná šířka, hlavní řádek + volitelný popisek druhým řádkem.
 */
export function VelkeTlacitko({
  varianta = 'primary',
  popisek,
  className = '',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  varianta?: VelkeVarianta;
  popisek?: string;
}) {
  return (
    <button
      type="button"
      {...props}
      className={`flex min-h-14 w-full flex-col items-center justify-center gap-0.5 rounded-xl px-4 py-2 transition-colors duration-100 disabled:cursor-not-allowed ${VELKE_VARIANTY[varianta]} ${className}`}
    >
      {/* Řádek s ikonou + textem (SVG ikony se nesmí lámat nad text). */}
      <span className="flex items-center justify-center gap-2 text-lg leading-tight font-bold">
        {children}
      </span>
      {popisek ? <span className="text-sm leading-tight font-normal">{popisek}</span> : null}
    </button>
  );
}

/**
 * Kancelářský dialog (Blok D) — plovoucí okno s libovolným obsahem (formuláře
 * kvalifikace, ZL editor, podpisy). Fluent vzor: overlay, surface, hlavička,
 * akce vpravo dole (`akce`).
 */
export function Dialog({
  nadpis,
  onZavrit,
  siroky = false,
  akce,
  children,
}: {
  nadpis: string;
  onZavrit: () => void;
  siroky?: boolean;
  akce?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-overlay p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={nadpis}
        className={`my-8 w-full ${siroky ? 'max-w-3xl' : 'max-w-lg'} rounded-lg bg-surface p-6 shadow-xl`}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-xl font-bold">{nadpis}</h2>
          <button
            type="button"
            aria-label="Zavřít"
            className="min-h-11 min-w-11 rounded-md text-2xl leading-none text-text-secondary transition-colors duration-100 hover:bg-bg"
            onClick={onZavrit}
          >
            ×
          </button>
        </div>
        {children}
        {akce ? <div className="mt-6 flex flex-wrap justify-end gap-3">{akce}</div> : null}
      </div>
    </div>
  );
}

/**
 * Potvrzovací dialog (podpis dne) — jednoduché plovoucí okno, dvě velká
 * tlačítka. Žádná knihovna, žádný focus-trap cirkus (POC).
 */
export function PotvrzovaciDialog({
  nadpis,
  text,
  potvrdit,
  probiha = false,
  onPotvrdit,
  onZrusit,
}: {
  nadpis: string;
  text: string;
  potvrdit: string;
  probiha?: boolean;
  onPotvrdit: () => void;
  onZrusit: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={nadpis}
        className="w-full max-w-md rounded-lg bg-surface p-5 shadow-xl"
      >
        <h2 className="mb-2 text-xl font-bold">{nadpis}</h2>
        <p className="mb-5 text-base text-text-secondary">{text}</p>
        <div className="flex flex-col gap-3">
          <VelkeTlacitko varianta="podpis" disabled={probiha} onClick={onPotvrdit}>
            {probiha ? 'Odesílám…' : potvrdit}
          </VelkeTlacitko>
          <VelkeTlacitko varianta="secondary" disabled={probiha} onClick={onZrusit}>
            Zrušit
          </VelkeTlacitko>
        </div>
      </div>
    </div>
  );
}
